import { useMemo, useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import Reveal from "@/components/base/Reveal";
import { comicChapters, dramaEpisodes, novelChapters, videoResources, type LibraryItem } from "@/mocks/siteData";

type TabKey = "novel" | "drama" | "comic" | "video";

type TabConfig = {
  key: TabKey;
  label: string;
  icon: string;
  note: string;
  items: LibraryItem[];
};

const tabs: TabConfig[] = [
  { key: "novel", label: "小说", icon: "ri-book-2-line", note: "按章节顺序排列，文字细节最完整，适合完整阅读。", items: novelChapters },
  { key: "drama", label: "短剧", icon: "ri-movie-2-line", note: "竖屏剧集，单集较短，适合用碎片时间连续追看。", items: dramaEpisodes },
  { key: "comic", label: "漫画", icon: "ri-image-2-line", note: "画面直观、分镜清晰，可与小说对照感受不同表达。", items: comicChapters },
  { key: "video", label: "视频", icon: "ri-play-circle-line", note: "解说与混剪为主，适合快速回顾主线剧情。", items: videoResources },
];

const INITIAL_COUNT = 6;

export default function ContentLibrary() {
  const [activeKey, setActiveKey] = useState<TabKey>("novel");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);

  const activeTab = tabs.find((tab) => tab.key === activeKey) ?? tabs[0];

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return activeTab.items;
    return activeTab.items.filter((item) => {
      return (
        item.title.toLowerCase().includes(keyword) ||
        item.summary.toLowerCase().includes(keyword) ||
        item.tags.some((tag) => tag.toLowerCase().includes(keyword))
      );
    });
  }, [activeTab, query]);

  const visible = expanded ? filtered : filtered.slice(0, INITIAL_COUNT);
  const hasMore = filtered.length > INITIAL_COUNT;

  const handleTabChange = (key: TabKey) => {
    setActiveKey(key);
    setExpanded(false);
  };

  return (
    <section id="library" className="scroll-mt-20 border-y border-background-200/70 bg-background-100">
      <div className="mx-auto w-full px-4 py-16 md:px-8 md:py-20">
        <Reveal>
          <SectionHeading
            eyebrow="内容目录"
            title="小说 · 短剧 · 漫画 · 视频 全集目录"
            description="下面按形式分类整理了全部条目。切换分类可以查看对应目录，搜索框只在当前分类内筛选，结果即时显示，无需跳转页面。"
          />
        </Reveal>

        <Reveal className="mt-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="inline-flex flex-wrap gap-1 self-start rounded-full border border-background-200 bg-background-50 p-1">
              {tabs.map((tab) => {
                const isActive = tab.key === activeKey;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => handleTabChange(tab.key)}
                    aria-pressed={isActive}
                    className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:bg-background-100 hover:text-primary-700"
                    }`}
                  >
                    <i className={tab.icon} />
                    {tab.label}
                    <span className={`text-xs ${isActive ? "text-background-50/80" : "text-foreground-400"}`}>
                      {tab.items.length}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="relative w-full lg:w-72">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                type="search"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setExpanded(false);
                }}
                placeholder={`在「${activeTab.label}」中搜索…`}
                aria-label={`在${activeTab.label}目录中搜索`}
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
          </div>

          <p className="mt-4 flex items-start gap-2 text-sm text-foreground-600">
            <i className="ri-information-line mt-0.5 text-primary-600" />
            {activeTab.note}
          </p>
        </Reveal>

        <Reveal className="mt-6">
          {visible.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-xl" />
              </span>
              <p className="mt-4 text-sm font-medium text-foreground-800">没有找到匹配的条目</p>
              <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或清空搜索查看该分类的全部目录。</p>
              <button
                type="button"
                onClick={() => setQuery("")}
                className="mt-5 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-refresh-line" />
                清空搜索
              </button>
            </div>
          ) : (
            <ul className="grid gap-4 lg:grid-cols-2">
              {visible.map((item, index) => (
                <li key={item.id}>
                  <Reveal delay={Math.min(index, 6) * 50}>
                    <article className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                        <span
                          className={`shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium ${
                            item.status === "最新"
                              ? "bg-accent-100 text-accent-800"
                              : "bg-secondary-100 text-secondary-800"
                          }`}
                        >
                          {item.status}
                        </span>
                      </div>
                      <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
                      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-background-200 pt-4">
                        {item.tags.map((tag) => (
                          <span key={tag} className="rounded-full bg-background-100 px-2.5 py-1 text-[11px] text-foreground-600">
                            {tag}
                          </span>
                        ))}
                        <span className="ml-auto text-[11px] text-foreground-400">{item.meta}</span>
                      </div>
                    </article>
                  </Reveal>
                </li>
              ))}
            </ul>
          )}
        </Reveal>

        {hasMore && visible.length > 0 ? (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
              {expanded ? "收起目录" : `展开全部 ${filtered.length} 条`}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}